import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import {
  PRESENTACION_MODE,
  PRESENTACION_PDF_URL,
  PRESENTACION_SLIDE_COUNT,
  PRESENTACION_VIDEO_URL,
  slideImageUrl,
} from "./crearMensajesPresentacionConfig";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const VIDEO_ACCEPT = "video/mp4,video/webm,video/quicktime";

const isVideoFile = (file) =>
  file?.type?.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(file?.name || "");

const sortImageFiles = (files) =>
  [...files].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

const PresentacionCrearMensajesDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`crearMensajes.presentation.${key}`, opts);

  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  const [staticVideoReady, setStaticVideoReady] = useState(false);
  const [staticPdfReady, setStaticPdfReady] = useState(false);
  const [localVideoUrl, setLocalVideoUrl] = useState(null);
  const [localPdfUrl, setLocalPdfUrl] = useState(null);
  const [localImageUrls, setLocalImageUrls] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const revokeUrls = useCallback((urls) => {
    urls.forEach((url) => {
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    });
  }, []);

  const clearLocalMedia = useCallback(() => {
    if (localVideoUrl) URL.revokeObjectURL(localVideoUrl);
    if (localPdfUrl) URL.revokeObjectURL(localPdfUrl);
    revokeUrls(localImageUrls);
    setLocalVideoUrl(null);
    setLocalPdfUrl(null);
    setLocalImageUrls([]);
  }, [localVideoUrl, localPdfUrl, localImageUrls, revokeUrls]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const check = async (url, setter) => {
      try {
        const res = await fetch(url, { method: "HEAD" });
        if (!cancelled) setter(res.ok);
      } catch {
        if (!cancelled) setter(false);
      }
    };
    check(PRESENTACION_VIDEO_URL, setStaticVideoReady);
    check(PRESENTACION_PDF_URL, setStaticPdfReady);
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(
    () => () => {
      if (localVideoUrl) URL.revokeObjectURL(localVideoUrl);
      if (localPdfUrl) URL.revokeObjectURL(localPdfUrl);
      revokeUrls(localImageUrls);
    },
    [localVideoUrl, localPdfUrl, localImageUrls, revokeUrls]
  );

  const videoSrc =
    localVideoUrl ||
    (PRESENTACION_MODE === "video" || staticVideoReady ? PRESENTACION_VIDEO_URL : null);

  const pdfSrc = localPdfUrl || (staticPdfReady ? PRESENTACION_PDF_URL : null);

  const staticImageUrls = useMemo(() => {
    if (PRESENTACION_MODE !== "images") return [];
    return Array.from({ length: PRESENTACION_SLIDE_COUNT }, (_, i) => slideImageUrl(i + 1));
  }, []);

  const imageUrls =
    localImageUrls.length > 0
      ? localImageUrls
      : PRESENTACION_MODE === "images"
        ? staticImageUrls
        : [];

  const useVideo = Boolean(videoSrc) && imageUrls.length === 0 && !localPdfUrl;
  const usePdf = !useVideo && imageUrls.length === 0 && Boolean(pdfSrc);
  const hasContent = useVideo || usePdf || imageUrls.length > 0;

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !isVideoFile(file)) return;
    clearLocalMedia();
    setLocalVideoUrl(URL.createObjectURL(file));
  };

  const handlePdfUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || file.type !== "application/pdf") return;
    clearLocalMedia();
    setLocalPdfUrl(URL.createObjectURL(file));
  };

  const handleImagesUpload = (event) => {
    const files = sortImageFiles(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    clearLocalMedia();
    setLocalImageUrls(files.map((file) => URL.createObjectURL(file)));
  };

  const closeLightbox = () => setLightboxIndex(null);

  const goLightbox = (delta) => {
    setLightboxIndex((current) => {
      if (current == null) return current;
      const next = current + delta;
      if (next < 0 || next >= imageUrls.length) return current;
      return next;
    });
  };

  useEffect(() => {
    if (lightboxIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goLightbox(-1);
      if (e.key === "ArrowRight") goLightbox(1);
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: "relative" }} color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} aria-label={p("close")}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 1, flex: 1 }} variant="h6" component="div" noWrap>
            {p("title")}
          </Typography>
          <input
            ref={videoInputRef}
            type="file"
            accept={VIDEO_ACCEPT}
            hidden
            onChange={handleVideoUpload}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={handlePdfUpload}
          />
          <input
            ref={imagesInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={handleImagesUpload}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<VideocamOutlinedIcon />}
              onClick={() => videoInputRef.current?.click()}
            >
              {p("uploadVideo")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => pdfInputRef.current?.click()}
            >
              {p("uploadPdf")}
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {!hasContent ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            bgcolor: "grey.50",
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 560, textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {p("emptyTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {p("emptyHint")}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<VideocamOutlinedIcon />}
              onClick={() => videoInputRef.current?.click()}
            >
              {p("uploadVideo")}
            </Button>
          </Stack>
        </Box>
      ) : useVideo ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#000",
            minHeight: "calc(100vh - 64px)",
            p: { xs: 1, sm: 2 },
          }}
        >
          <Box
            component="video"
            key={videoSrc}
            src={videoSrc}
            controls
            playsInline
            sx={{
              width: "100%",
              maxWidth: 1200,
              maxHeight: "calc(100vh - 80px)",
              outline: "none",
            }}
          />
        </Box>
      ) : usePdf ? (
        <Box
          component="iframe"
          title={p("title")}
          src={pdfSrc}
          sx={{ flex: 1, width: "100%", border: 0, minHeight: "calc(100vh - 64px)" }}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            bgcolor: "grey.100",
            p: { xs: 1.5, sm: 2.5 },
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto" }}>
            {imageUrls.map((src, index) => (
              <Box
                key={src}
                component="button"
                type="button"
                onClick={() => setLightboxIndex(index)}
                sx={{
                  display: "block",
                  width: "100%",
                  p: 0,
                  border: "none",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "zoom-in",
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ display: "block", px: 1.5, py: 0.75, fontWeight: 700 }}
                >
                  {p("slide", { n: index + 1 })}
                </Typography>
                <Box
                  component="img"
                  src={src}
                  alt={p("slide", { n: index + 1 })}
                  loading="lazy"
                  sx={{ width: "100%", height: "auto", display: "block" }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {lightboxIndex != null && imageUrls[lightboxIndex] ? (
        <Box
          onClick={closeLightbox}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            bgcolor: "rgba(0,0,0,0.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "common.white",
              bgcolor: "rgba(255,255,255,0.12)",
            }}
            aria-label={p("close")}
          >
            <CloseIcon />
          </IconButton>
          {lightboxIndex > 0 ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                goLightbox(-1);
              }}
              sx={{
                position: "absolute",
                left: 8,
                color: "common.white",
                bgcolor: "rgba(255,255,255,0.12)",
              }}
              aria-label={p("prev")}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          ) : null}
          {lightboxIndex < imageUrls.length - 1 ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                goLightbox(1);
              }}
              sx={{
                position: "absolute",
                right: 8,
                color: "common.white",
                bgcolor: "rgba(255,255,255,0.12)",
              }}
              aria-label={p("next")}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          ) : null}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: "min(1200px, 96vw)", maxHeight: "90vh" }}
          >
            <Typography
              variant="caption"
              sx={{ color: "common.white", display: "block", mb: 1, textAlign: "center" }}
            >
              {p("slide", { n: lightboxIndex + 1 })} ({lightboxIndex + 1}/{imageUrls.length})
            </Typography>
            <Box
              component="img"
              src={imageUrls[lightboxIndex]}
              alt={p("slide", { n: lightboxIndex + 1 })}
              sx={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
            />
          </Box>
        </Box>
      ) : null}
    </Dialog>
  );
};

export default PresentacionCrearMensajesDialog;
